import React, { useEffect, useState } from "react";
import { LogIn, MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { LAUNCH_DATE } from "@/lib/site-config";
import { loginUrl } from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");

function useCountdown(target) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(0, target.getTime() - now);
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    const seconds = Math.floor((diff % 60_000) / 1000);
    return { days, hours, minutes, seconds, done: diff === 0 };
}

export default function ComingSoon() {
    const { user, loading } = useAuth();
    const c = useCountdown(LAUNCH_DATE);

    const teamLoginUrl = `${loginUrl}?next=/`;

    return (
        <div className="min-h-screen bg-brand-cream relative overflow-hidden grain" data-testid="coming-soon-page">
            {/* Decorative blurs */}
            <div aria-hidden className="absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full bg-brand-mint/40 blur-3xl pointer-events-none" />
            <div aria-hidden className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-brand-sage/30 blur-3xl pointer-events-none" />

            {/* Top bar */}
            <header className="relative z-10">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
                    <img src="/brand/logo-300.webp" alt="Pharmacie La Lutrive" className="h-14 md:h-16 w-auto" />
                    {!loading && (user ? (
                        <a href="/" onClick={() => window.location.reload()} className="hidden sm:inline-flex items-center gap-2 ll-link-underline text-sm text-brand-primary">
                            ✓ Connecté · voir le site
                        </a>
                    ) : (
                        <a href={teamLoginUrl} className="hidden sm:inline-flex items-center gap-2 text-xs font-sans text-brand-ink/60 hover:text-brand-primary transition-colors">
                            <LogIn className="h-3.5 w-3.5" />
                            Accès équipe
                        </a>
                    ))}
                </div>
            </header>

            <main className="relative z-10">
                <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-12 pb-24 md:pt-20 md:pb-32 text-center">
                    <p className="ll-eyebrow text-brand-primary mb-8 animate-fade-up">
                        Lutry · Canton de Vaud · Suisse
                    </p>

                    <h1 className="font-display text-[3rem] sm:text-[4rem] lg:text-[6rem] leading-[0.95] text-brand-ink tracking-tight animate-fade-up">
                        Bientôt à votre <br />
                        <span className="italic text-brand-primary">service</span>
                        <span className="text-brand-primary">.</span>
                    </h1>

                    <p className="mt-10 max-w-xl mx-auto text-lg md:text-xl text-brand-ink/70 font-sans leading-relaxed animate-fade-up">
                        Une nouvelle pharmacie indépendante ouvre ses portes au cœur du Lavaux —
                        avec l'écoute, le conseil et le savoir-faire artisanal qui font notre métier.
                    </p>

                    {/* Date */}
                    <div className="mt-14 inline-flex flex-col items-center animate-fade-up">
                        <span className="ll-divider text-brand-primary mb-4">Ouverture</span>
                        <p className="font-display text-[2rem] sm:text-[2.5rem] md:text-[3.25rem] leading-tight text-brand-ink">
                            <span className="italic">mercredi</span> 1<sup className="text-base align-super">er</sup> juillet 2026
                        </p>
                    </div>

                    {/* Countdown */}
                    {!c.done && (
                        <div className="mt-14 grid grid-cols-4 gap-3 sm:gap-6 max-w-2xl mx-auto animate-fade-up">
                            <CountdownCell value={c.days} label="jours" />
                            <CountdownCell value={pad(c.hours)} label="heures" />
                            <CountdownCell value={pad(c.minutes)} label="minutes" />
                            <CountdownCell value={pad(c.seconds)} label="secondes" />
                        </div>
                    )}

                    {/* Brand divider */}
                    <div className="my-20 flex items-center justify-center gap-3 text-brand-primary/50">
                        <span className="h-px w-12 bg-current" />
                        <svg className="h-5 w-12" viewBox="0 0 60 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M0 10 Q 7.5 0, 15 10 T 30 10 T 45 10 T 60 10" />
                        </svg>
                        <span className="h-px w-12 bg-current" />
                    </div>

                    {/* Contact placeholder */}
                    <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto text-left">
                        <Tile icon={<MapPin className="h-4 w-4" />} title="Adresse" value={<>Grand-Rue 53<br />1095 Lutry</>} />
                        <Tile icon={<Phone className="h-4 w-4" />} title="Téléphone" value="021 791 53 53" />
                        <Tile icon={<Mail className="h-4 w-4" />} title="Email" value={<a href="mailto:pharmacie@lalutrive.ch" className="hover:text-brand-primary">pharmacie@lalutrive.ch</a>} />
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="relative z-10 border-t border-brand-line/70 bg-brand-cream/70 backdrop-blur-sm">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-xs font-sans text-brand-ink/55">
                        © {new Date().getFullYear()} Pharmacie La Lutrive — Site en cours de construction.
                    </p>
                    <div className="flex items-center gap-3 text-brand-ink/55">
                        <a href="#" aria-label="Instagram" className="h-9 w-9 rounded-full border border-brand-line inline-flex items-center justify-center hover:bg-brand-primary hover:text-brand-cream hover:border-brand-primary transition-colors">
                            <Instagram className="h-4 w-4" />
                        </a>
                        <a href="#" aria-label="Facebook" className="h-9 w-9 rounded-full border border-brand-line inline-flex items-center justify-center hover:bg-brand-primary hover:text-brand-cream hover:border-brand-primary transition-colors">
                            <Facebook className="h-4 w-4" />
                        </a>
                        <a href={teamLoginUrl} className="ml-3 text-xs inline-flex items-center gap-1.5 hover:text-brand-primary" data-testid="team-login-link">
                            <LogIn className="h-3 w-3" /> Accès équipe
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

const CountdownCell = ({ value, label }) => (
    <div className="bg-white border border-brand-line py-5 px-2 sm:py-7 shadow-sm">
        <p className="font-display text-3xl sm:text-5xl text-brand-primary leading-none">{value}</p>
        <p className="ll-eyebrow text-brand-ink/50 mt-2 text-[10px] sm:text-xs">{label}</p>
    </div>
);

const Tile = ({ icon, title, value }) => (
    <div className="bg-white/70 border border-brand-line/70 backdrop-blur p-5">
        <div className="flex items-center gap-3 mb-2 text-brand-primary">
            <span className="h-8 w-8 rounded-full bg-brand-mint/40 inline-flex items-center justify-center">{icon}</span>
            <span className="ll-eyebrow text-brand-ink/55">{title}</span>
        </div>
        <p className="font-sans text-brand-ink leading-snug">{value}</p>
    </div>
);
